import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { toast } from "react-hot-toast";
import axiosInstance from "../../axiosInstance";
import { useBlogContext } from "../../fetchdata/BlogContext";

const Contact = () => {
  const { Headers, isHeader } = useBlogContext();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, maxLength } = e.target;
    // Enforce maxlength visually without custom CSS
    setFormData((prev) => ({ ...prev, [name]: value.slice(0, maxLength || value.length) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axiosInstance.post("/contact-enquire", formData);
      toast.success("Email sent successfully");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Failed to send email:", error);
      toast.error("Failed to send email");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header />

      {/* Hero */}
      <section className="bg-light py-5 border-bottom">
        <div className="container">
          <div className="row align-items-center g-4">
            <div className="col-lg-12">
              <h1 className="display-5 fw-bold mb-3">Contact Us</h1>
              <p className="lead text-muted mb-0">
                We’d love to hear from you. Share your query and our team will get back to you soon.
              </p>
            </div>
            
          </div>
        </div>
      </section>

      {/* Contact + Info */}
      <section className="py-5">
        <div className="container">
          <div className="row g-4">
            {/* Left: Form */}
            <div className="col-lg-7">
              <div className="card shadow-sm h-100">
                <div className="card-body p-4 p-lg-5">
                  <h2 className="h3 mb-3">Send us a message</h2>
                  <p className="text-muted mb-4">
                    Fill in the form and we’ll respond as soon as possible.
                  </p>

                  <form className="needs-validation" onSubmit={handleSubmit} noValidate>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label htmlFor="name" className="form-label">Name</label>
                        <input
                          id="name"
                          type="text"
                          className="form-control"
                          name="name"
                          placeholder="Your full name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          maxLength={40}
                        />
                        <div className="invalid-feedback">Please enter your name.</div>
                      </div>

                      <div className="col-md-6">
                        <label htmlFor="email" className="form-label">Email</label>
                        <input
                          id="email"
                          type="email"
                          className="form-control"
                          name="email"
                          placeholder="name@example.com"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          maxLength={60}
                        />
                        <div className="invalid-feedback">Please enter a valid email.</div>
                      </div>

                      <div className="col-md-6">
                        <label htmlFor="phone" className="form-label">Phone</label>
                        <input
                          id="phone"
                          type="tel"
                          className="form-control"
                          name="phone"
                          placeholder="10-digit mobile number"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          maxLength={15}
                          pattern="^[0-9+\-\s()]{7,}$"
                        />
                        <div className="form-text">You can include +, -, or spaces.</div>
                        <div className="invalid-feedback">Please enter a valid phone number.</div>
                      </div>

                      <div className="col-12">
                        <label htmlFor="message" className="form-label">Message</label>
                        <textarea
                          id="message"
                          className="form-control"
                          name="message"
                          rows={5}
                          placeholder="How can we help?"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          maxLength={500}
                        />
                        <div className="invalid-feedback">Please write a message.</div>
                        <div className="d-flex justify-content-end">
                          <small className="text-muted mt-1">
                            {formData.message.length}/500
                          </small>
                        </div>
                      </div>

                      <div className="col-12">
                        <button
                          className="btn btn-primary w-100 d-flex align-items-center justify-content-center"
                          type="submit"
                          disabled={submitting}
                        >
                          {submitting && (
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            />
                          )}
                          {submitting ? "Sending..." : "Submit"}
                        </button>
                      </div>
                    </div>
                  </form>

                  {/* Small reassurance strip */}
                  <div className="row g-3 mt-4">
                    <div className="col-12 col-sm-4">
                      <div className="border rounded-3 p-3 text-center">
                        <div className="fw-semibold">Response within 24h</div>
                      </div>
                    </div>
                    <div className="col-12 col-sm-4">
                      <div className="border rounded-3 p-3 text-center">
                        <div className="fw-semibold">Secure & private</div>
                      </div>
                    </div>
                    <div className="col-12 col-sm-4">
                      <div className="border rounded-3 p-3 text-center">
                        <div className="fw-semibold">Customer-first support</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Contact Info + FAQ */}
            <div className="col-lg-5">
              <div className="card shadow-sm mb-4">
                <div className="card-body p-4">
                  <h3 className="h5 mb-3">Reach us directly</h3>
                  {!isHeader ? (
                    <ul className="list-group list-group-flush">
                      <li className="list-group-item px-0">
                        <div className="small text-muted">Phone</div>
                        <div className="fw-semibold">+91 {Headers.phone}</div>
                      </li>
                      <li className="list-group-item px-0">
                        <div className="small text-muted">Email</div>
                        <div className="fw-semibold">{Headers.email}</div>
                      </li>
                      <li className="list-group-item px-0">
                        <div className="small text-muted">Address</div>
                        <div className="fw-semibold">{Headers.address}</div>
                      </li>
                    </ul>
                  ) : (
                    <div className="alert alert-light mb-0">Loading contact info…</div>
                  )}

                  <div className="d-grid gap-2 mt-3">
                    <a href={`tel:+91${!isHeader ? Headers.phone : ""}`} className="btn btn-outline-primary">
                      Call Now
                    </a>
                    <a href={`mailto:${!isHeader ? Headers.email : ""}`} className="btn btn-outline-secondary">
                      Email Us
                    </a>
                  </div>
                </div>
              </div>

              <div className="card shadow-sm">
                <div className="card-body p-4">
                  <h3 className="h5 mb-3">FAQs</h3>
                  <div className="accordion" id="faqAccordion">
                    <div className="accordion-item">
                      <h2 className="accordion-header" id="faq1">
                        <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#faqOne" aria-expanded="true" aria-controls="faqOne">
                          How soon will you reply?
                        </button>
                      </h2>
                      <div id="faqOne" className="accordion-collapse collapse show" data-bs-parent="#faqAccordion">
                        <div className="accordion-body">
                          We aim to respond within one business day.
                        </div>
                      </div>
                    </div>
                    <div className="accordion-item">
                      <h2 className="accordion-header" id="faq2">
                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faqTwo" aria-expanded="false" aria-controls="faqTwo">
                          Do you offer on-site support?
                        </button>
                      </h2>
                      <div id="faqTwo" className="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                        <div className="accordion-body">
                          Yes, depending on your location and requirement. Share details in the form.
                        </div>
                      </div>
                    </div>
                    <div className="accordion-item">
                      <h2 className="accordion-header" id="faq3">
                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faqThree" aria-expanded="false" aria-controls="faqThree">
                          What information should I include?
                        </button>
                      </h2>
                      <div id="faqThree" className="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                        <div className="accordion-body">
                          Briefly describe your need, preferred contact time, and your city/sector.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Map */}
          <div className="row g-4 mt-4">
            <div className="col-12">
              <div className="card shadow-sm">
                <div className="card-body p-0">
                  <div className="ratio ratio-21x9">
                    <iframe
                      title="Location Map"
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d471220.5631094339!2d88.04952462217592!3d22.6757520733225!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39f882db4908f667%3A0x43e330e68f6c2cbc!2sKolkata%2C%20West%20Bengal!5e0!3m2!1sen!2sin!4v1596988408134!5m2!1sen!2sin"
                      allowFullScreen
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="row mt-4">
            <div className="col-12">
              <div className="alert alert-primary d-flex align-items-center justify-content-between">
                <div>
                  <strong>Need urgent help?</strong> Call us and mention “Website Contact” for priority support.
                </div>
                <a
                  href={`tel:+91${!isHeader ? Headers.phone : ""}`}
                  className="btn btn-primary"
                >
                  Call Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Contact;
